import { Component, ReactNode, ErrorInfo } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error("Error boundary caught:", error, errorInfo);
    }
    // TODO: Send to error tracking (Sentry, etc.)
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || <ChunkErrorFallback error={this.state.error} />
      );
    }
    return this.props.children;
  }
}

export function ChunkErrorFallback({ error }: { error?: Error }) {
  const isChunkError =
    error?.name === "ChunkLoadError" ||
    error?.message?.includes("Failed to fetch dynamically imported module");

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg p-6 text-center">
        <h2 className="text-xl font-semibold text-white mb-4">
          {isChunkError ? "Loading Failed" : "Something Went Wrong"}
        </h2>
        <p className="text-gray-300 mb-6">
          {isChunkError
            ? "Unable to load this page. Please check your connection and try again."
            : "An unexpected error occurred. Please refresh the page."}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-teal-500 text-white px-6 py-2 rounded-md hover:bg-teal-600"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}
